<template>
  <div class="lf-section__toolbar">
    <div
      v-if="!state.config.clone"
      class="lf-section__toolbar-item lf-section__toolbar-item--insert-block lf-section__toolbar-item--active"
    >
      <a class="lf-section__toolbar-item-btn" @click.stop="insertBlock"><i class="icon-plus-small"></i></a>
    </div>

    <div
      class="lf-section__toolbar-item lf-section__toolbar-item--move-up"
      :class="{ 'lf-section__toolbar-item--active': canMoveUp }"
    >
      <a class="lf-section__toolbar-item-btn" @click="moveUp"><i class="icon-arrow-up"></i></a>
    </div>

    <div
      class="lf-section__toolbar-item lf-section__toolbar-item--move-down"
      :class="{ 'lf-section__toolbar-item--active': canMoveDown }"
    >
      <a class="lf-section__toolbar-item-btn" @click="moveDown"><i class="icon-arrow-down"></i></a>
    </div>

    <div
      v-if="!state.config.global"
      class="lf-section__toolbar-item lf-section__toolbar-item--delete lf-section__toolbar-item--active"
    >
      <a class="lf-section__toolbar-item-btn" @click="handleDelete"><i class="icon-trash"></i></a>
    </div>
  </div>

  <Modal v-if="showContentAddModal" @closeModal="showContentAddModal = false">
    <template #modal-heading>Add new section</template>
    <template #modal-content>
      <ModalContentAddContent
        :context-model="model"
        :type="BlockTypes.BLOCKS"
        @closeModal="showContentAddModal = false"
      />
    </template>
  </Modal>

  <teleport to="body">
    <transition name="modal">
      <Prompt
        v-if="showPrompt"
        :heading="promptHeading"
        :description="promptDescription"
        :buttons="promptButtons"
        @buttonClicked="handlePromptButtonClicked"
        @hidePrompt="showPrompt = false"
      />
    </transition>
  </teleport>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from 'vue';
import Prompt from '@/components/components/prompt/Prompt.vue';
import Modal from '@/components/components/modals/Modal.vue';
import ModalContentAddContent from '@/components/components/editing/Modals/ModalContentAddContent.vue';
import { useStore } from '@/store';
import { BlockTypes, modalButtonId, SectionModelType } from '@/typings/types/types';
import { CampaignState } from '@/models/CampaignModel';
import { SectionBaseModel } from '@/components/layout/section/SectionBaseModel';
import { ModalButton } from '@/components/components/editing/Modals/interfaces';

export default defineComponent({
  name: 'SectionEditToolbar',
  components: {
    ModalContentAddContent,
    Modal,
    Prompt
  },
  props: {
    model: {
      type: Object as PropType<SectionModelType>,
      required: true
    }
  },
  setup(props) {
    const state = (props.model as SectionBaseModel).state;
    const showPrompt = ref(false);
    const promptHeading = ref<string | undefined>();
    const promptDescription = ref<string | undefined>();
    const promptButtons = ref<ModalButton[]>([]);
    const showContentAddModal = ref(false);

    const store = useStore();
    const campaignState = store.getters['campaign/state'] as CampaignState;

    const insertBlock = () => {
      showContentAddModal.value = true;
    };

    const hide = () => {
      props.model.saveBlock();
      showPrompt.value = false;
    };

    const moveUp = () => {
      props.model.onMoveUp();
      props.model.saveBlock();
    };

    const moveDown = () => {
      props.model.onMoveDown();
      props.model.saveBlock();
    };

    /**
     * The reason for naming this handleDelete, is that "delete" is a reserved javascript word
     */
    const handleDelete = () => {
      const elementState = state;
      let msg: string | undefined;
      if (elementState.config?.hasRegistrationAddon && campaignState.config && campaignState.config.hasRegistrations) {
        msg =
          'This campaign already has registrations therefore you are not able to delete this block because it include a registration form.';

        if (msg) {
          promptDescription.value = msg;
        }
        promptButtons.value = [
          {
            id: 'no',
            variant: 'text',
            label: 'Close'
          }
        ];

        showPrompt.value = true;
        return;
      }
      msg = 'Are you sure you wish to delete this section?';

      if (msg) {
        promptDescription.value = msg;
      }

      promptButtons.value = [
        {
          id: 'yes',
          variant: 'text',
          label: 'Yes, delete'
        },
        {
          id: 'no',
          variant: 'text',
          label: 'No, cancel'
        }
      ];

      showPrompt.value = true;
    };

    const handlePromptButtonClicked = (id: modalButtonId) => {
      if (id === 'yes') {
        props.model.delete();
        hide();
      }
      if (id === 'no') {
        hide();
      }
    };

    const canMoveDown = computed(() => {
      return props.model.canMoveDown();
    });

    const canMoveUp = computed(() => {
      return props.model.canMoveUp();
    });

    return {
      state,
      insertBlock,
      moveUp,
      moveDown,
      handleDelete,
      canMoveDown,
      canMoveUp,
      showPrompt,
      showContentAddModal,
      BlockTypes,
      promptHeading,
      promptDescription,
      promptButtons,
      handlePromptButtonClicked
    };
  }
});
</script>

<style lang="scss">
.lf-section {
  &__toolbar {
    display: block;
    position: absolute;
    left: 100px;
    top: 0px;
    z-index: 20;

    &-item {
      display: block;
      float: left;
      padding-right: 1px;
      pointer-events: none;

      &--active {
        pointer-events: all;

        .lf-section__toolbar-item-btn {
          transition: color 125ms linear, background-color 125ms linear;
          color: #959595 !important;
        }
      }

      &-btn {
        display: block;
        width: 25px;
        height: 25px;
        background: #232424;
        line-height: 25px;
        cursor: pointer;
        text-align: center;
        font-size: 14px;
        text-decoration: none !important;

        &.lf-section__toolbar-item-btn {
          color: darken(#959595, 30%) !important;
        }

        &:hover {
          color: #fff !important;
          background: lighten(#232424, 5%);
        }

        .lf-section__toolbar-item--open & {
          background: #003fff;
          color: #fff !important;

          &:hover {
            background: lighten(#003fff, 5%);
          }
        }

        .icon-plus-small {
          position: relative;
          top: -1px;
          font-size: 9px;
        }

        .icon-arrow-up,
        .icon-arrow-down {
          display: inline-block;
          position: relative;
          top: -1px;
          font-size: 7px;
        }

        .icon-arrow-up {
          display: inline-block;
          position: relative;
          top: -4px;
          transform: rotate(180deg);
        }

        .icon-trash {
          display: inline-block;
          position: relative;
          top: -1px;
          font-size: 11px;
        }

        .icon-more {
          display: inline-block;
          position: relative;
          top: -3px;
          transform: scale(0.5);
          font-size: 3px;
        }
      }
    }

    &::after {
      content: '';
      display: block;
      clear: both;
      line-break: normal;
      font-size: 0;
    }
  }
}
</style>
